/**
 * A table's column order, remembered per table in this browser, and the
 * drag that changes it.
 *
 * `DataTable` uses it for every table it draws. The rules are in
 * `column-order-model.ts`, where they are tested. What is here is the state,
 * the storage and the HTML drag events on a header cell, the way Blockcircle's
 * tables move a column: pick a header up, let it go over another, and it lands
 * on the side of that header the pointer was on.
 *
 * A DRAG THAT STARTS ON A RESIZE HANDLE IS A RESIZE. The handle lies over the
 * header's right edge, inside a cell that is now draggable, so a press on the
 * handle is remembered in the capture phase and the drag the browser would
 * start from it is cancelled.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { DragEvent, PointerEvent } from 'react'
import {
  columnOrderStore,
  dropSide,
  isMovableColumn,
  isReordered,
  moveColumn,
  orderColumns,
  readColumnOrder,
  stepColumn,
  writeColumnOrder,
  type OrderableColumn,
} from './column-order-model'

/** Where a header being dragged over would put the column. */
export interface DropMark {
  key: string
  side: 'before' | 'after'
}

/** The props a movable header cell spreads. */
export interface HeaderDragProps {
  draggable: true
  onPointerDownCapture: (event: PointerEvent<HTMLTableCellElement>) => void
  onDragStart: (event: DragEvent<HTMLTableCellElement>) => void
  onDragOver: (event: DragEvent<HTMLTableCellElement>) => void
  onDragLeave: (event: DragEvent<HTMLTableCellElement>) => void
  onDrop: (event: DragEvent<HTMLTableCellElement>) => void
  onDragEnd: () => void
}

export interface ColumnOrderApi<T> {
  /** The page's columns in the order the person left them. */
  ordered: T[]
  /** True when the order differs from the page's own. */
  reordered: boolean
  /** The column being carried, while a drag is on. */
  dragging: string | null
  /** Where the column being carried would land, while it is over a header. */
  mark: DropMark | null
  /** The drag props for a header, or null for a column that does not move. */
  headerDrag: (key: string) => HeaderDragProps | null
  /** One step left or right, for the columns picker. */
  step: (key: string, step: -1 | 1) => void
  /** The page's own order back, and the stored one forgotten. */
  reset: () => void
}

/** The type the drag carries, so a drop from anything else (a file, a link) is ignored. */
export const COLUMN_DRAG_TYPE = 'application/x-gojibasel-column'

export function useColumnOrder<T extends OrderableColumn>(tableKey: string, columns: readonly T[]): ColumnOrderApi<T> {
  const [stored, setStored] = useState<string[]>(() => readColumnOrder(columnOrderStore(), tableKey))
  useEffect(() => {
    setStored(readColumnOrder(columnOrderStore(), tableKey))
  }, [tableKey])

  const [dragging, setDragging] = useState<string | null>(null)
  const [mark, setMark] = useState<DropMark | null>(null)
  // Set by a press on a resize handle, read by the drag that press would start.
  const pressedHandle = useRef(false)

  const ordered = useMemo(() => orderColumns(columns, stored), [columns, stored])
  const reordered = useMemo(() => isReordered(columns, stored), [columns, stored])
  const movable = useMemo(
    () => new Set(columns.filter((column, index) => isMovableColumn(column, index)).map((column) => column.key)),
    [columns],
  )

  const commit = useCallback(
    (next: string[]) => {
      setStored(next)
      writeColumnOrder(columnOrderStore(), tableKey, next)
    },
    [tableKey],
  )

  const finish = useCallback(() => {
    setDragging(null)
    setMark(null)
    pressedHandle.current = false
  }, [])

  const headerDrag = useCallback(
    (key: string): HeaderDragProps | null => {
      if (!movable.has(key)) return null
      return {
        draggable: true,
        onPointerDownCapture: (event) => {
          pressedHandle.current = event.target instanceof Element && event.target.closest('[role="separator"]') !== null
        },
        onDragStart: (event) => {
          if (pressedHandle.current) {
            event.preventDefault()
            return
          }
          event.dataTransfer.effectAllowed = 'move'
          event.dataTransfer.setData(COLUMN_DRAG_TYPE, key)
          // Firefox starts no drag without a plain-text payload.
          event.dataTransfer.setData('text/plain', key)
          setDragging(key)
        },
        onDragOver: (event) => {
          if (dragging === null || !event.dataTransfer.types.includes(COLUMN_DRAG_TYPE)) return
          event.preventDefault()
          event.dataTransfer.dropEffect = 'move'
          if (dragging === key) {
            if (mark !== null) setMark(null)
            return
          }
          const box = event.currentTarget.getBoundingClientRect()
          const side = dropSide(event.clientX, box.left, box.width)
          if (mark?.key !== key || mark.side !== side) setMark({ key, side })
        },
        onDragLeave: (event) => {
          // Leaving for a child of the same cell is not leaving the cell.
          const into = event.relatedTarget
          if (into instanceof Node && event.currentTarget.contains(into)) return
          if (mark?.key === key) setMark(null)
        },
        onDrop: (event) => {
          if (dragging === null || !event.dataTransfer.types.includes(COLUMN_DRAG_TYPE)) return
          event.preventDefault()
          const box = event.currentTarget.getBoundingClientRect()
          const side = dropSide(event.clientX, box.left, box.width)
          if (dragging !== key) commit(moveColumn(columns, stored, dragging, key, side))
          finish()
        },
        onDragEnd: finish,
      }
    },
    [movable, dragging, mark, columns, stored, commit, finish],
  )

  const step = useCallback((key: string, by: -1 | 1) => commit(stepColumn(columns, stored, key, by)), [columns, stored, commit])
  const reset = useCallback(() => commit([]), [commit])

  return { ordered, reordered, dragging, mark, headerDrag, step, reset }
}
